<template>
  <section>
    <h2>{{ editMode ? "تعديل موظف" : "إضافة موظف" }}</h2>

    <Toolbar class="mb-4">
      <template #start>
        <Button icon="pi pi-save" label="حفظ" @click="saveEmployee" />
        <Button class="ml-2" icon="pi pi-times" label="إلغاء" severity="secondary" @click="router.back()" />
      </template>
      <template #end>
        <Tag :value="editMode ? 'تعديل' : 'جديد'" severity="info" />
      </template>
    </Toolbar>

    <Card class="mb-4">
      <template #title>البيانات الأساسية</template>
      <template #content>
        <div class="grid md:grid-cols-3 gap-3">
          <div>
            <label class="block mb-1 text-sm">الإسم الأول</label>
            <InputText v-model.trim="form.first_name" placeholder="الإسم الأول" required />
          </div>
          <div>
            <label class="block mb-1 text-sm">اسم الأب</label>
            <InputText v-model.trim="form.mid_name" placeholder="اسم الأب" />
          </div>
          <div>
            <label class="block mb-1 text-sm">اسم العائلة</label>
            <InputText v-model.trim="form.last_name" placeholder="اسم العائلة" />
          </div>
          <div>
            <label class="block mb-1 text-sm">تاريخ الميلاد</label>
            <Calendar v-model="birthdayDate" showIcon dateFormat="yy-mm-dd" />
          </div>
          <div>
            <label class="block mb-1 text-sm">النوع</label>
            <InputText v-model.trim="form.gender" placeholder="" />
          </div>
          <div>
            <label class="block mb-1 text-sm">الحاله الإجتماعيه</label>
            <InputText v-model.trim="form.merital_status" placeholder="" />
          </div>
          <div>
            <label class="block mb-1 text-sm">التعليم</label>
            <InputText v-model.trim="form.school" placeholder="" />
          </div>
          <div>
            <label class="block mb-1 text-sm">أسماء فيسبوك</label>
            <div v-if="facebookNames.length" class="facebook-name-list border rounded-md p-2 text-sm">
              <div v-for="(name, idx) in facebookNames" :key="`fb-name-${idx}`" class="py-0.5">
                {{ name }}
              </div>
            </div>
            <div v-else class="text-color-secondary text-sm">لا توجد أسماء مسجلة</div>
          </div>
        </div>
      </template>
    </Card>

    <Card class="mb-4">
      <template #title>الوسوم</template>
      <template #content>
        <label class="block mb-1 text-sm">هاشتاغات الموظف</label>
        <Chips v-model="form.hashtags" class="w-full" separator="," :allowDuplicate="false" />
        <small class="text-color-secondary block mt-1">
          أضف الوسوم بدون علامة # لربط الموظف بنفس وسوم الويكي.
        </small>
      </template>
    </Card>

    <fieldset>
      <label>Photo:</label>
      <Card class="mb-4">
        <template #title>الصور</template>
        <template #content>
          <div
            class="photo-drop-zone"
            :class="{ 'is-drag-over': isPhotoDropActive }"
            @dragenter.prevent="onPhotoDragEnter"
            @dragover.prevent="onPhotoDragOver"
            @dragleave.prevent="onPhotoDragLeave"
            @drop.prevent="onPhotoDrop"
          >
            <FileUpload
              name="photos"
              accept="image/*"
              :multiple="true"
              :auto="false"
              :customUpload="true"
              chooseLabel="اختر صور"
              uploadLabel="رفع"
              cancelLabel="إلغاء"
              @uploader="onPhotosUpload"
            />
            <p class="mt-2 text-sm text-center text-color-secondary">
              اسحب الصور وأفلتها هنا أو استخدم زر الإختيار
            </p>
          </div>
          <div v-if="photos.length" class="mt-3 text-sm">
            <strong>الصور المختارة:</strong>
            <ul class="list-disc pr-5 mt-2">
              <li v-for="file in photos" :key="`${file.name}-${file.size}-${file.lastModified}`">
                {{ file.name }}
              </li>
            </ul>
          </div>
        </template>
      </Card>
    </fieldset>

    <!-- Edit mode: show existing images with delete checkboxes -->
    <div v-if="props.editMode && images.length">
      <fieldset>
        <legend>صور متاحة</legend>
        <div v-for="imgId in images" :key="imgId" style="display:inline-block;margin:5px;text-align:center;">
          <img :src="imageUrl(imgId)" width="100" alt="Employee photo" /><br />
          <label>
            <input type="checkbox" :value="imgId" v-model="deleteImageIds" /> Delete
          </label>
        </div>
      </fieldset>
    </div>

    <!-- Company -->
    <MasterDataForm
      v-model="form.company_id"
      endpoint="/api/companies"
      idKey="company_id"
      label="الشركة"
      name="company"
      :fields="[{ name: 'company_name', label: 'اسم الشركة', required: true }]"
      :formatItem="(c: any) => c.company_name"
    />

    <!-- Department -->
    <MasterDataForm
      v-model="form.department_id"
      endpoint="/api/departments"
      idKey="department_id"
      label="القسم"
      name="department"
      :fields="[{ name: 'department_name', label: 'اسم القسم', required: true }]"
      :formatItem="(d: any) => d.department_name"
    />

    <!-- Title -->
    <MasterDataForm
      v-model="form.title_id"
      endpoint="/api/titles"
      idKey="title_id"
      label="المنصب"
      name="title"
      :fields="[{ name: 'title', label: 'المنصب', required: true }]"
      :formatItem="(t: any) => t.title"
    />

    <!-- Office -->
    <MasterDataForm
      v-model="form.office_id"
      endpoint="/api/offices"
      idKey="office_id"
      label="المكتب"
      name="office"
      :fields="[{ name: 'office_name', label: 'اسم المكتب', required: true }]"
      :formatItem="(o: any) => o.office_name"
    />

    <!-- Rank -->
    <MasterDataForm
      v-model="form.rank_id"
      :endpoint="`/api/ranks?company_id=${form.company_id ?? ''}`"
      idKey="rank_id"
      label="الرتبة"
      name="rank"
      :fields="[
        { name: 'rank_name', label: 'اسم الرتبة', required: true }
      ]"
      :formatItem="(r: any) => `${r.rank_name} (Company ${r.company_id})`"
      :disabled="!form.company_id"
      :extraPayload="() => ({ company_id: form.company_id })"
    />

    <Card class="mb-4">
      <template #title>جهات الاتصال</template>
      <template #content>
        <div class="grid md:grid-cols-3 gap-3">
          <div>
            <label class="block mb-1 text-sm">هاتف الشركة</label>
            <InputText v-model.trim="form.office_phone" placeholder="..." />
          </div>
          <div>
            <label class="block mb-1 text-sm">إيميل الشركة</label>
            <InputText v-model.trim="form.office_email" type="email" />
          </div>
        </div>
      </template>
    </Card>

    <Card class="mb-4">
      <template #title>بيانات شخصيه</template>
      <template #content>
        <div class="grid md:grid-cols-3 gap-3">
          <div>
            <label class="block mb-1 text-sm">رقم الجوال</label>
            <InputText v-model.trim="form.mobile" placeholder="رقم الجوال" />
          </div>
          <div>
            <label class="block mb-1 text-sm">هاتف المنزل</label>
            <InputText v-model.trim="form.phone" placeholder="هاتف المنزل" />
          </div>
          <div>
            <label class="block mb-1 text-sm">الإيميل الشخصي</label>
            <InputText v-model.trim="form.personal_email" type="email" placeholder="الإيميل الشخصي" />
          </div>
        </div>
      </template>
    </Card>

    <label>عنوان المنزل</label><br />
    <Card class="mb-4">
      <template #title>عنوان المنزل</template>
      <template #content>
        <div class="grid md:grid-cols-3 gap-3">
          <div class="md:col-span-3">
            <label class="block mb-1 text-sm">العنوان</label>
            <InputText v-model.trim="form.address" />
          </div>
          <div>
            <label class="block mb-1 text-sm">الدولة</label>
            <InputText v-model.trim="form.country" />
          </div>
          <div>
            <label class="block mb-1 text-sm">المدينة</label>
            <InputText v-model.trim="form.city" />
          </div>
          <div>
            <label class="block mb-1 text-sm">المحافظة</label>
            <InputText v-model.trim="form.state" />
          </div>
          <div>
            <label class="block mb-1 text-sm">الرمز البريدي</label>
            <InputText v-model.trim="form.postal_code" />
          </div>
        </div>
      </template>
    </Card>

    <Card class="mb-6">
      <template #title>دفتر الملاحظات</template>
      <template #content>
        <div v-if="!form.notes.length" class="text-color-secondary text-sm mb-3">
          لا توجد ملاحظات بعد. استخدم الزر بالأسفل لإضافة ملاحظة جديدة.
        </div>
        <div
          v-for="(note, idx) in form.notes"
          :key="`note-${idx}`"
          class="note-entry border rounded-lg p-3 mb-3"
        >
          <div class="grid md:grid-cols-4 gap-3">
            <div>
              <label class="block mb-1 text-sm font-medium">التاريخ</label>
              <InputText type="date" v-model="note.date" class="w-full" />
            </div>
            <div class="md:col-span-3 flex flex-col gap-1">
              <label class="text-sm font-medium">الوصف</label>
              <Textarea v-model="note.note" :autoResize="true" rows="2" class="w-full" />
            </div>
          </div>
          <div class="flex justify-end mt-2">
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              size="small"
              @click="removeNote(idx)"
            />
          </div>
        </div>
        <Button icon="pi pi-plus" label="إضافة ملاحظة" text @click="addNote" />
      </template>
    </Card>

    <EmployeeRelationshipsManager
      v-if="editMode"
      :employeeId="props.employeeId ?? Number(route.params.id as string)"
    />

    <FacebookProfilesManager :employeeId="props.employeeId ?? Number(route.params.id as string)" />
  </section>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue"
import type { components } from '@/types/api'

import Toolbar from 'primevue/toolbar'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import Calendar from 'primevue/calendar'
import FileUpload from 'primevue/fileupload'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown';
import Chips from 'primevue/chips'

import type { FileUploadUploaderEvent } from 'primevue/fileupload'
import axios from "axios"

import MasterDataForm from "@/components/MasterDataForm.vue"
import FacebookProfilesManager from '@/components/facebook/FacebookProfilesManager.vue'
import EmployeeRelationshipsManager from '@/components/employee/EmployeeRelationshipsManager.vue'
import { useRoute, useRouter } from "vue-router"
import { useFlashStore } from '@/store/flash'



const IS_DEV = (window as any).IS_DEV === true

type Employee = any
type NoteEntry = {
  date: string | null
  note: string
}

type EmployeeAssoc = {
  company_id: number | null
  department_id: number | null
  title_id: number | null
  office_id: number | null
  rank_id: number | null
  office_phone: string | null
  office_email: string | null
  notes?: NoteEntry[]
}
type ImageItem = number
type FbRow = {
  _key: string
  employee_id: number
  profile_id?: number
  profile_url: string | null
  username: string | null
  fb_id?: string | null
  profile_title: string | null
  company_name: string | null
  job_title: string | null
  address_1: string | null
  address_2: string | null
  _dirty: boolean
  _deleted: boolean
}

const loading = ref<boolean>(false)
const employee = ref<Employee | null>(null)
const facebookNames = ref<string[]>([])

const props = defineProps({ editMode: Boolean, employeeId: Number })
const form = ref<{
  first_name: string
  mid_name: string
  last_name: string
  hashtags: string[]
  company_id: number | undefined
  department_id: number | undefined
  title_id: number | undefined
  office_id: number | undefined
  rank_id: number | undefined
  office_phone: string | null
  office_email: string | null
  mobile: string | null
  phone: string | null
  personal_email: string | null
  address: string
  country_code: string | null
  city: string
  state: string
  postal_code: string | null
  country: string
  birthday: string | null
  gender: string | null
  merital_status: string | null
  school: string | null
  notes: NoteEntry[]
}>({
  first_name: "",
  mid_name: "",
  last_name: "",
  hashtags: [] as string[],
  company_id: undefined,
  department_id: undefined,
  title_id: undefined,
  office_id: undefined,
  rank_id: undefined,
  office_phone: null,
  office_email: null,
  mobile: null,
  phone: null,
  personal_email: null,
  address: "",
  country_code: null,
  city: "",
  state: "",
  postal_code: null,
  country: "",
  birthday: null,
  gender: null,
  merital_status: null,
  school: null,
  notes: [] as NoteEntry[]
})

// Reset rank when company is unselected
watch(() => form.value.company_id, (val) => {
  if (!val) form.value.rank_id = undefined
})

const router = useRouter()
const route = useRoute()
const photos = ref<File[]>([])            // newly selected files
const images = ref<ImageItem[]>([])       // existing images (edit mode)
const deleteImageIds = ref<number[]>([])  // ids to delete
const isPhotoDropActive = ref(false)      // drag state for new uploads
const flash = useFlashStore()
const editMode = computed(() => !!route.params.id)


const birthdayDate = computed<Date | null>({
  get: () => (form.value.birthday ? new Date(form.value.birthday) : null),
  set: (d) => { form.value.birthday = d ? d.toISOString().slice(0, 10) : null }
})

function onPhotosUpload(event: FileUploadUploaderEvent) {
  const incoming = filesToImageArray(event?.files as File | File[] | FileList | undefined)
  if (!incoming.length) return
  photos.value = photos.value.concat(incoming)
}

function imageUrl(imageId: number) {
  return `/api/employees/${props.employeeId}/images/${imageId}.png`
}

function filesToImageArray(files: File | File[] | FileList | undefined | null): File[] {
  if (!files) return []
  const array = files instanceof FileList ? Array.from(files)
    : Array.isArray(files) ? files
    : [files]
  return array.filter((file) => file && file.type?.startsWith("image/"))
}

function dragContainsFiles(event: DragEvent) {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files")
}

function onPhotoDragEnter(event: DragEvent) {
  if (!dragContainsFiles(event)) return
  isPhotoDropActive.value = true
}

function onPhotoDragOver(event: DragEvent) {
  if (!dragContainsFiles(event)) return
  if (!isPhotoDropActive.value) isPhotoDropActive.value = true
}

function onPhotoDragLeave(event: DragEvent) {
  if (!dragContainsFiles(event)) return
  const currentTarget = event.currentTarget as HTMLElement | null
  const related = event.relatedTarget as Node | null
  if (currentTarget && related && currentTarget.contains(related)) return
  isPhotoDropActive.value = false
}

function onPhotoDrop(event: DragEvent) {
  const droppedFiles = filesToImageArray(event.dataTransfer?.files ?? null)
  if (!droppedFiles.length) {
    isPhotoDropActive.value = false
    return
  }
  isPhotoDropActive.value = false
  photos.value = photos.value.concat(droppedFiles)
}

function addNote() {
  form.value.notes.push({
    date: new Date().toISOString().slice(0, 10),
    note: '',
  })
}

function removeNote(index: number) {
  form.value.notes.splice(index, 1)
}

async function loadEmployeeForEdit() {
  if (!props.editMode) return
  const id = Number(props.employeeId)

  const [{ data: details }, { data: base }] = await Promise.all([
    axios.get(`/api/employees/${id}/details`),
    axios.get(`/api/employees/${id}`),
  ])

  // Base person fields
  form.value.first_name     = base.first_name ?? ""
  form.value.mid_name       = base.mid_name ?? ""
  form.value.last_name      = base.last_name ?? ""
  form.value.personal_email = base.personal_email ?? ""
  form.value.mobile         = base.mobile ?? ""
  form.value.phone          = base.phone ?? ""
  form.value.address        = base.address ?? ""
  form.value.country_code   = base.country_code ?? ""
  form.value.city           = base.city ?? ""
  form.value.state          = base.state ?? ""
  form.value.postal_code    = base.postal_code ?? ""
  form.value.country        = base.country ?? ""
  form.value.birthday       = base.birthday || null
  form.value.gender         = base.gender || null
  form.value.merital_status = base.merital_status || null
  form.value.school         = base.school
  form.value.hashtags = Array.isArray(base.hashtags)
    ? base.hashtags
        .map((tag: any) => (tag?.label ?? "").trim())
        .filter((label: string) => !!label)
    : []
  facebookNames.value = Array.isArray(base.facebook_names)
    ? [...base.facebook_names]
    : []

  // Associations: take primary (first)
  const assoc = (details?.companies?.[0] ?? {}) as Partial<EmployeeAssoc>
  form.value.company_id     = assoc.company_id != null ? Number(assoc.company_id) : undefined
  form.value.department_id  = assoc.department_id != null ? Number(assoc.department_id) : undefined
  form.value.title_id       = assoc.title_id != null ? Number(assoc.title_id) : undefined
  form.value.office_id      = assoc.office_id != null ? Number(assoc.office_id) : undefined
  form.value.rank_id        = assoc.rank_id != null ? Number(assoc.rank_id) : undefined
  form.value.office_phone   = assoc.office_phone ?? null
  form.value.office_email   = assoc.office_email ?? null

  const loadedNotes: NoteEntry[] = Array.isArray((assoc as any)?.notes)
    ? ((assoc as any).notes as any[]).map((note) => ({
        date: note?.date || null,
        note: note?.note || '',
      }))
    : [];
  form.value.notes.length = 0;
  loadedNotes.forEach((entry) => form.value.notes.push(entry));

  // Normalize ids
  (["company_id","department_id","title_id","office_id","rank_id"] as const).forEach(
    (k: 'company_id'|'department_id'|'title_id'|'office_id'|'rank_id') => {
    const v = (form.value as any)[k]
    ;(form.value as any)[k] = (v === undefined || v === "") ? undefined : Number(v)
  })
}

function ensureBackGoesToSearch() {
  if (route.query.rs !== "1") {
    router.replace({ path: "/employee/all" })
    const newQuery = { ...route.query, rs: "1" as const }
    router.push({ path: `${route.path ?? ''}`, query: newQuery })
  }
}

async function loadImages() {
  if (!props.editMode) return
  const { data } = await axios.get(`/api/employees/${props.employeeId}/images`)
  images.value = (data ?? [])
  .map((d: any) =>
    typeof d === 'number' ? d : Number(d.id ?? d.image_id ?? d)
  )
  .filter((n: number) => Number.isFinite(n)) as ImageItem[]
}

onMounted(() => {
  ensureBackGoesToSearch()
  loadEmployeeForEdit()
  loadImages()
})

function normalizeEmployeePayload(raw: typeof form.value): EmployeeAssoc & typeof form.value {
  const payload: any = { ...raw }
  delete payload.notes

  // convert empty strings to null for optional fields
  const toNull = [
    "birthday", "office_phone", "office_email",
    "mobile", "phone", "personal_email",
    "address", "country_code", "city", "state",
    "postal_code", "country", "gender", "merital_status", "school"
  ]
  toNull.forEach((k) => { if (payload[k] === "") payload[k] = null })

  // cast select ids ("" -> null, else Number)
  ;["company_id", "department_id", "title_id", "office_id", "rank_id"].forEach((k) => {
    payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k])
  })

  if (!payload.birthday) payload.birthday = null
  payload.hashtags = normalizeHashtags(raw.hashtags)
  return payload
}

function normalizeHashtags(values?: string[] | null): string[] {
  if (!Array.isArray(values)) return []
  const seen = new Set<string>()
  const normalized: string[] = []
  values.forEach((raw) => {
    const clean = (raw ?? "").trim().replace(/^#/, "")
    if (!clean) return
    const key = clean.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    normalized.push(clean)
  })
  return normalized
}

function normalizedNotes(): NoteEntry[] {
  return form.value.notes
    .map((note) => ({
      date: note.date || null,
      note: (note.note || '').trim(),
    }))
    .filter((note) => note.note.length > 0)
}

async function saveEmployee() {
  let step: string = "start"
  try {
    const body = normalizeEmployeePayload(form.value)

    if (props.editMode) {
      // 1) Update core employee fields
      step = "PUT /api/employees/:id"
      await axios.put(`/api/employees/${props.employeeId}`, body)

      // 2) Update associations
      step = "PUT /api/employees/:id/associations"
      const assoc: EmployeeAssoc = {
        company_id: body.company_id,
        department_id: body.department_id,
        title_id: body.title_id,
        office_id: body.office_id,
        rank_id: body.rank_id,
        office_phone: body.office_phone,
        office_email: body.office_email,
        notes: normalizedNotes(),
      }
      await axios.put(`/api/employees/${props.employeeId}/associations`, assoc)

      // 3) Delete images (optional)
      step = "DELETE images"
      if (deleteImageIds.value.length) {
        await axios.delete(`/api/employees/${props.employeeId}/images`, {
          params: { ids: deleteImageIds.value }
        })
      }

      // 4) Upload new images (optional)
      step = "UPLOAD images"
      if (photos.value.length) {
        const fd = new FormData()
        photos.value.forEach((f: File) => fd.append("files", f))
        await axios.post(`/api/employees/${props.employeeId}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        })
      }

      // 5) Go back to view with banner
      step = "NAVIGATE"
      flash.set('تم التحديث بنجاح')
      router.push({ path: `/employee/${props.employeeId}` })

    } else {
      // CREATE
      step = "POST /api/employees"
      const { data } = await axios.post("/api/employees", body)
      const id: number = Number(data.employee_id)

      // Create associations if any were chosen
      step = "PUT /api/employees/:id/associations"
      const assoc: EmployeeAssoc = {
        company_id: body.company_id,
        department_id: body.department_id,
        title_id: body.title_id,
        office_id: body.office_id,
        rank_id: body.rank_id,
        office_phone: body.office_phone,
        office_email: body.office_email,
        notes: normalizedNotes(),
      }
      await axios.put(`/api/employees/${id}/associations`, assoc)

      // Upload images
      step = "UPLOAD images"
      if (photos.value.length) {
        const fd = new FormData()
        photos.value.forEach((f: File) => fd.append("files", f))
        await axios.post(`/api/employees/${id}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        })
      }

      // Save FB profiles (optional)
      step = "NAVIGATE"
      const targetId = editMode.value ? Number(route.params.id as string) : id
      if (targetId && fbHasChanges.value) {
        try {
          await fbSaveAll(targetId)
        } catch {
          flash.set('تم حفظ الموظف، لكن فشل 保存 حسابات فيسبوك. يمكنك المحاولة لاحقًا من شاشة التعديل.')
        }
      }
      flash.set('تمت الإضافة بنجاح')
      router.push({ path: `/employee/${id}` })
    }
  } catch (err) {
    if (IS_DEV) {
      console.error(`[saveEmployee] Failed at step: ${step}`, err)
      alert(`(DEV) خطأ أثناء الحفظ عند الخطوة: ${step}`)
    } else {
      alert(((err as any)?.response?.data?.detail) ?? "خطأ أثناء الحفظ")
    }
  }
}

///////////////////////
//// FACEBOOK STUFF
///////////////////////
const fbLoaded = ref(false)
const fbSaving = ref(false)
const fbRows = ref<FbRow[]>([])
const fbHasChanges = computed(() =>
  fbRows.value.some((r: FbRow) => r._deleted || !r.profile_id || r._dirty)
)

watch(fbRows, () => {
  fbRows.value.forEach((r: FbRow) => { if (!r._deleted) r._dirty = true })
}, { deep: true })

function fbPayloadFromRow(r: FbRow, eid: number) {
  return {
    employee_id: eid,
    fb_id: r.fb_id ?? null,
    profile_url: r.profile_url || null,
    profile_title: r.profile_title || null,
    username: r.username || null,
    company_name: r.company_name || null,
    job_title: r.job_title || null,
    address_1: r.address_1 || null,
    address_2: r.address_2 || null,
  }
}

async function onFbToggle(open: boolean) {
  if (!open || !editMode.value || fbLoaded.value) return
  try {
    const empId = Number(route.params.id as string)
    const { data } = await axios.get(`/api/employees/${empId}/facebook-profiles`)
    fbRows.value = (data || []).map((d: any) => ({ ...d, _key: `p-${d.profile_id}`, _dirty: false, _deleted: false })) as FbRow[]
  } catch {
    fbRows.value = []
  } finally {
    fbLoaded.value = true
  }
}

// function fbAddRow() {
//   const key = `tmp-${Math.random().toString(36).slice(2)}`
//   const eid = Number(route.params.id as string) || 0
//   fbRows.value.push({
//     _key: key,
//     employee_id: eid,
//     profile_url: "",
//     username: "",
//     fb_id: undefined,
//     profile_title: "",
//     company_name: "",
//     job_title: "",
//     address_1: "",
//     address_2: "",
//     _dirty: true,
//     _deleted: false,
//   } as FbRow)
// }

// function fbToggleDelete(idx: number) {
//   const r = fbRows.value[idx] as FbRow
//   r._deleted = !r._deleted
//   r._dirty = true
// }

// function fbDiscardLocal() {
//   fbLoaded.value = false
//   fbRows.value = []
// }

/** Save profiles for a specific employee id. */
async function fbSaveAll(overrideEmployeeId: number | string | null = null) {
  // derive employee id robustly
  let eid = Number(route.params.id as string)
  if (typeof overrideEmployeeId === 'number' && isFinite(overrideEmployeeId)) {
    eid = overrideEmployeeId
  } else if (typeof overrideEmployeeId === 'string' && /^\d+$/.test(overrideEmployeeId)) {
    eid = Number(overrideEmployeeId)
  }
  if (!eid) return

  fbSaving.value = true
  try {
    // deletes first
    for (const r of fbRows.value.filter((r: FbRow) => r.profile_id && r._deleted)) {
      await axios.delete(`/api/employees/${eid}/facebook-profiles/${r.profile_id}`)
    }
    // upserts
    for (const r of fbRows.value.filter((r: FbRow) => !r._deleted)) {
      const payload = fbPayloadFromRow(r, eid)
      if (r.profile_id) {
        if (r._dirty) {
          const { data } = await axios.patch(
            `/api/employees/${eid}/facebook-profiles/${r.profile_id}`,
            payload
          )
          Object.assign(r, data)
        }
      } else {
        const { data } = await axios.post(
          `/api/employees/${eid}/facebook-profiles`,
          payload
        )
        Object.assign(r, data)
      }
      r._dirty = false
    }
    // normalize UI
    fbRows.value = fbRows.value
      .filter((r: FbRow) => !r._deleted)
      .map((r: FbRow) => ({ ...r, _key: r.profile_id ? `p-${r.profile_id}` : r._key })) as FbRow[]
    useFlashStore().set('تم حفظ حسابات فيسبوك')
  } catch (e: any) {
    const status = e?.response?.status
    const detail = e?.response?.data?.detail
    alert(detail ? `${detail}` : `فشل حفظ حسابات فيسبوك.${status ? ' (HTTP '+status+')' : ''}`)
    if (IS_DEV) console.error('FB save failed', e?.response || e)
  } finally {
    fbSaving.value = false
    fbLoaded.value = true
  }
}
</script>

<style scoped>
.photo-drop-zone {
  border: 2px dashed var(--p-primary-color, #0ea5e9);
  border-radius: 0.75rem;
  padding: 1rem;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.photo-drop-zone.is-drag-over {
  border-color: var(--p-primary-600, #0369a1);
  background-color: rgba(14, 165, 233, 0.08);
}

.note-entry {
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 0.75rem;
}

.facebook-name-list {
  max-height: 160px;
  overflow-y: auto;
  background: var(--surface-card, #fff);
}
</style>
